import { motion, useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { data, useActionData } from 'react-router'
import { BookDemoTabbedForm } from '#app/components/book-demo-tabbed-form.tsx'
import {
	FeatureGrid,
	MarketingButton,
	MarketingHero,
	MarketingPageShell,
	MarketingSection,
	SplitProof,
	type MarketingFeature,
} from '#app/components/marketing-page-shell.tsx'
import { PriceDropAlertIllustration } from '#app/components/price-drop-alert-illustration.tsx'
import { dealerWebsiteSchema } from '#app/utils/constants.ts'
import { processSalesLeadSubmission } from '#app/utils/marketing-form.server.ts'
import { mediaAsset, mediaUrl } from '#app/utils/media.ts'
import { type Route } from './+types/car-dealer-websites.ts'

const MetaTitle = `High-Converting Car Dealer Websites | Drive More Sales`
const MetaDescription = `Transform your dealership with modern, mobile-first websites built to convert. Powerful inventory management, lead generation tools, and seamless CRM integration to help you sell more cars.`

const HeroImageSrc = 'carena-motors-mockup.png'
const directScheduleNamespace = 'website-demo-direct'
const SearchSpeedVideoSrc =
	'https://media.dealerproedge.com/DPE-Search-Speed.mp4'
// Increase the height to zoom in and crop more from the left and bottom.
const SearchSpeedVideoHeight = '102.5%'
// Increase this value to crop more from the left; use a negative value to reveal more.
const SearchSpeedVideoRightOffset = '0%'
const AiInventoryImageSrc = mediaAsset(
	'/images/car-dealer-websites/audi-rsq8-performance.png',
)
const AiVehicleDescriptions = [
	'Meet the 2026 Audi RS Q8 performance, where a 631-hp 4.0-liter biturbo V8 and quattro all-wheel drive launch this five-passenger SUV from 0–60 mph in 3.4 seconds. Standard ceramic brakes and RS-tuned adaptive air suspension deliver confident control without giving up everyday comfort.',
	'Settle into Valcona leather sport seats with heating and ventilation, then tailor the 12.3-inch Audi virtual cockpit plus to your drive. The 2026 Audi RS Q8 performance pairs serious speed with a quiet, premium cabin, generous SUV versatility, and technology designed for daily life.',
	'From sweeping corners to the daily commute, the 2026 Audi RS Q8 performance stays composed with active roll stabilization, a quattro rear sport differential, and adaptive air suspension. A top-view camera with Virtual 360 and available adaptive cruise assist add confidence when the pace slows.',
] as const
const AiDescriptionStartDelay = 700
const AiDescriptionWordDelay = 55
const AiDescriptionDeleteDelay = 24
const AiDescriptionHoldDelay = 2000

type AiDescriptionPhase = 'waiting' | 'typing' | 'holding' | 'deleting'

export const handle = { chrome: 'payload' }

const features: MarketingFeature[] = [
	{
		label: '01',
		title: 'Inventory that feels instant',
		description:
			'Fast SRPs, clean VDPs, dynamic filters, and multi-location inventory sync help shoppers find the right vehicle without waiting on bloated pages.',
	},
	{
		label: '02',
		title: 'Built to convert shoppers',
		description:
			'Lead capture, calls, forms, price-drop alerts, and clear vehicle details are designed around the next best action instead of generic brochure layouts.',
	},
	{
		label: '03',
		title: 'Performance by default',
		description:
			'Core Web Vitals, responsive design, structured data, secure hosting, and uptime-focused infrastructure are part of the platform foundation.',
	},
	{
		label: '04',
		title: 'Dealer-ready operations',
		description:
			'CRM integrations, unlimited inventory exports, analytics, and support workflows keep your website connected to how the store actually works.',
	},
	{
		label: '05',
		title: 'SEO fundamentals included',
		description:
			'Local SEO essentials, schema, inventory pages, and market-aware content structures give your dealership a stronger search foundation from launch.',
	},
	{
		label: '06',
		title: 'Room to grow',
		description:
			'Start with a high-performance website, then add segmentation, automated marketing, design studio support, and dedicated account management.',
	},
]

const connectedPlatformFeatures = [
	{
		label: '01',
		title: 'Help shoppers find the right vehicle',
		description:
			'Fast search, dynamic filters, and synchronized inventory create a clear path from your homepage to the vehicles shoppers want.',
	},
	{
		label: '02',
		title: 'Turn interest into action',
		description:
			'Responsive pages, focused calls to action, and price-drop alerts keep shoppers moving toward a form, phone call, or showroom visit.',
	},
	{
		label: '03',
		title: 'See what drives results',
		description:
			'Google Analytics and ProEdge reporting connect website activity to the insights your team needs to make smarter marketing decisions.',
	},
]

function SearchSpeedLaptop() {
	const shouldReduceMotion = Boolean(useReducedMotion())
	const { inView, ref } = useInView({ threshold: 0.35 })

	return (
		<div
			aria-label="Vehicle search results narrowing instantly as a shopper selects filters"
			className="mx-auto w-full max-w-[720px] lg:w-[86%]"
			ref={ref}
			role="img"
		>
			<div className="mx-auto w-full" style={{ perspective: '1400px' }}>
				<motion.div
					animate={{
						opacity: inView ? 1 : 0.76,
						rotateX: inView || shouldReduceMotion ? 0 : -72,
					}}
					className="relative mx-auto w-[94%] rounded-t-[18px] bg-neutral-100 p-[6px] shadow-[0_22px_44px_-28px_rgba(0,0,0,0.72)] ring-1 ring-black/15 sm:p-2"
					initial={false}
					style={{
						transformOrigin: 'bottom',
						transformStyle: 'preserve-3d',
					}}
					transition={{
						duration: shouldReduceMotion ? 0 : 0.72,
						ease: [0.22, 1, 0.36, 1],
					}}
				>
					<div
						className="relative overflow-hidden rounded-t-[12px] bg-white"
						style={{ aspectRatio: '16 / 9' }}
					>
						<motion.div
							animate={{
								filter: inView ? 'blur(0px)' : 'blur(7px)',
								opacity: inView ? 1 : 0,
							}}
							className="absolute inset-0 overflow-hidden"
							initial={false}
							transition={{
								delay: inView && !shouldReduceMotion ? 0.42 : 0,
								duration: shouldReduceMotion ? 0 : 0.2,
								ease: 'easeOut',
							}}
						>
							{inView ? (
								<video
									aria-hidden="true"
									autoPlay
									className="absolute top-0 w-auto max-w-none"
									loop
									muted
									playsInline
									preload="metadata"
									src={SearchSpeedVideoSrc}
									style={{
										height: SearchSpeedVideoHeight,
										right: SearchSpeedVideoRightOffset,
									}}
								/>
							) : null}
						</motion.div>
					</div>
				</motion.div>
				<div className="relative h-4 w-full rounded-b-[28px] rounded-t-md bg-gradient-to-b from-neutral-100 to-neutral-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_14px_24px_-18px_rgba(0,0,0,0.8)]">
					<div className="absolute inset-x-0 top-0 mx-auto h-2 w-[16%] rounded-b-md bg-neutral-400 shadow-[inset_0_-1px_0_rgba(255,255,255,0.45)]" />
				</div>
			</div>
		</div>
	)
}

function AiInventoryVisual() {
	const shouldReduceMotion = Boolean(useReducedMotion())
	const { inView, ref } = useInView({
		threshold: 0.28,
		triggerOnce: true,
	})
	const [descriptionIndex, setDescriptionIndex] = useState(0)
	const [visibleWordCount, setVisibleWordCount] = useState(0)
	const [descriptionPhase, setDescriptionPhase] =
		useState<AiDescriptionPhase>('waiting')
	const currentDescription =
		AiVehicleDescriptions[descriptionIndex] ?? AiVehicleDescriptions[0]
	const descriptionWords = currentDescription.split(' ')
	const visibleWords = shouldReduceMotion
		? descriptionWords
		: descriptionWords.slice(0, visibleWordCount)
	const showCursor =
		inView && !shouldReduceMotion && descriptionPhase !== 'waiting'

	useEffect(() => {
		if (!inView || shouldReduceMotion) return

		const delay =
			descriptionPhase === 'waiting'
				? AiDescriptionStartDelay
				: descriptionPhase === 'typing'
					? AiDescriptionWordDelay
					: descriptionPhase === 'holding'
						? AiDescriptionHoldDelay
						: AiDescriptionDeleteDelay

		const timeout = window.setTimeout(() => {
			if (descriptionPhase === 'waiting') {
				setDescriptionPhase('typing')
				return
			}

			if (descriptionPhase === 'typing') {
				if (visibleWordCount < descriptionWords.length) {
					setVisibleWordCount((count) => count + 1)
				} else {
					setDescriptionPhase('holding')
				}
				return
			}

			if (descriptionPhase === 'holding') {
				setDescriptionPhase('deleting')
				return
			}

			if (visibleWordCount > 0) {
				setVisibleWordCount((count) => count - 1)
				return
			}

			setDescriptionIndex((index) => (index + 1) % AiVehicleDescriptions.length)
			setDescriptionPhase('typing')
		}, delay)

		return () => window.clearTimeout(timeout)
	}, [
		descriptionPhase,
		descriptionWords.length,
		inView,
		shouldReduceMotion,
		visibleWordCount,
	])

	return (
		<div className="relative min-h-[580px] sm:min-h-[520px]" ref={ref}>
			<motion.img
				alt="Chilli Red Audi RS Q8 performance outside a modern dealership showroom"
				animate={{
					opacity: inView ? 1 : 0,
					scale: inView || shouldReduceMotion ? 1 : 0.94,
					x: inView || shouldReduceMotion ? 0 : -28,
				}}
				className="absolute left-0 top-0 w-full max-w-none object-contain sm:-left-[4%] sm:bottom-2 sm:top-auto sm:w-[108%]"
				initial={false}
				loading="lazy"
				src={AiInventoryImageSrc}
				transition={{
					duration: shouldReduceMotion ? 0 : 0.7,
					ease: [0.22, 1, 0.36, 1],
				}}
			/>

			<motion.div
				animate={{
					opacity: inView ? 1 : 0,
					scale: inView || shouldReduceMotion ? 1 : 0.88,
					y: inView || shouldReduceMotion ? 0 : 28,
				}}
				className="absolute right-0 top-[275px] z-10 w-[calc(100%-1rem)] max-w-[420px] border border-black bg-[#fffffa] shadow-[12px_12px_0_rgba(0,0,0,0.16)] sm:right-0 sm:top-0 sm:w-[60%]"
				initial={false}
				transition={{
					delay: inView && !shouldReduceMotion ? 0.36 : 0,
					duration: shouldReduceMotion ? 0 : 0.48,
					ease: [0.22, 1, 0.36, 1],
				}}
			>
				<div className="flex items-center border-b border-black px-4 py-3">
					<div className="flex items-center gap-2">
						<span
							aria-hidden="true"
							className="h-2 w-2 animate-pulse rounded-full bg-[#EC1C23]"
						/>
						<span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
							AI description
						</span>
					</div>
				</div>
				<p
					aria-label={currentDescription}
					className="min-h-[142px] p-4 text-sm leading-6 text-black sm:min-h-[166px] sm:p-5 sm:text-[15px] sm:leading-7"
				>
					<span aria-hidden="true">
						{visibleWords.map((word, index) => (
							<motion.span
								animate={{ filter: 'blur(0px)', opacity: 1 }}
								className="inline"
								initial={{ filter: 'blur(4px)', opacity: 0 }}
								key={`${descriptionIndex}-${word}-${index}`}
								transition={{
									duration: shouldReduceMotion ? 0 : 0.16,
								}}
							>
								{word}{' '}
							</motion.span>
						))}
						{showCursor ? (
							<motion.span
								animate={{
									opacity: descriptionPhase === 'holding' ? [1, 0, 1] : 1,
								}}
								className="ml-0.5 inline-block h-[1.15em] w-[2px] translate-y-[0.18em] bg-[#EC1C23]"
								initial={{ opacity: 0 }}
								key={`${descriptionIndex}-${descriptionPhase}`}
								transition={{
									duration: descriptionPhase === 'holding' ? 0.8 : 0.12,
									repeat: descriptionPhase === 'holding' ? Infinity : 0,
								}}
							/>
						) : null}
					</span>
				</p>
			</motion.div>
		</div>
	)
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/car-dealer-websites'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ property: 'og:image', content: mediaUrl(HeroImageSrc) },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': dealerWebsiteSchema },
	]
}

export async function action(args: Route.ActionArgs) {
	const formData = await args.request.formData()
	const result = await processSalesLeadSubmission(
		formData,
		'website-demo',
		args,
	)

	if (!result.success) {
		return data(
			{ result: result.submission },
			{
				status: result.status,
				headers: 'headers' in result ? result.headers : undefined,
			},
		)
	}

	return data({ success: true })
}

export default function CarDealerWebsitesPage() {
	const actionData = useActionData<typeof action>()
	const didSubmit = actionData && 'success' in actionData && actionData.success
	const lastResult =
		actionData && 'result' in actionData ? actionData.result : undefined

	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Dealer Website Platform"
				title="Your website should sell cars."
				subtitle="A slow, outdated website costs you sales every day. DealerProEdge gives you a lightning-fast, mobile-first platform that turns browsers into buyers and gives your team cleaner tools to manage inventory, leads, and performance."
				imageSrc={mediaUrl(HeroImageSrc)}
				imageAlt="DealerProEdge website mockup"
				stats={[
					{ value: '100', label: 'Core Web Vitals mindset' },
					{ value: '99.9%', label: 'Uptime target' },
					{ value: '24/7', label: 'Inventory visibility' },
				]}
				actions={
					<>
						<MarketingButton to="#getStarted">Schedule My Demo</MarketingButton>
						<MarketingButton to="/automotive-seo" variant="light">
							See SEO
						</MarketingButton>
					</>
				}
			/>

			<MarketingSection
				eyebrow="Website essentials"
				title="Everything your digital showroom needs to move faster."
			>
				<FeatureGrid features={features} />
			</MarketingSection>

			<MarketingSection>
				<div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center lg:gap-14">
					<div className="lg:col-span-7">
						<AiInventoryVisual />
					</div>
					<div className="lg:col-span-5">
						<p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#EC1C23]">
							AI-enhanced inventory
						</p>
						<h2 className="mt-5 text-[clamp(2.55rem,4.25vw,4.75rem)] font-medium leading-[0.96] tracking-[-0.04em]">
							Attract more customers with AI-enhanced inventory
						</h2>
						<div className="text-black/64 mt-7 space-y-5 text-base leading-7 sm:text-lg sm:leading-8">
							<p>
								DealerProEdge uses advanced AI models to turn vehicle data into
								compelling, on-brand descriptions that give every VDP more
								reason to be read—and more reason for shoppers to act.
							</p>
							<p>
								As inventory changes, fresh content is created automatically for
								each listing. The result is richer, more useful vehicle pages at
								scale—without asking your team to write every description from
								scratch.
							</p>
						</div>
					</div>
				</div>
			</MarketingSection>

			<MarketingSection>
				<SplitProof
					kicker="Price-drop urgency"
					title="Turn every price drop into a return visit."
					media={<PriceDropAlertIllustration />}
					unframedMedia
					body={
						<>
							<p>
								Every vehicle that receives a price reduction can be marked
								automatically, showing shoppers exactly how much the price has
								dropped right on the listing.
							</p>
							<p>
								Shoppers can sign up for email alerts with a single click. When
								the price moves, the notification brings them back while they
								are most motivated to buy.
							</p>
						</>
					}
				/>
			</MarketingSection>

			<MarketingSection>
				<div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center lg:gap-10">
					<div className="lg:col-span-7">
						<SearchSpeedLaptop />
					</div>
					<div className="lg:col-span-5">
						<h2 className="text-[clamp(2.75rem,4.5vw,5.25rem)] font-medium leading-[0.94] tracking-[-0.04em]">
							Accelerate the search
						</h2>
						<p className="text-black/64 mt-7 text-lg leading-8">
							Speed wins, so our websites are engineered to accelerate vehicle
							search from seconds to milliseconds—instantly narrowing results
							with each letter typed and each detail tapped, including payment
							range, features, fuel type, color, and more.
						</p>
						<div className="mt-9 flex max-w-[34rem] items-center gap-5 border-l-4 border-[#EC1C23] py-3 pl-5 sm:gap-7 sm:pl-7">
							<p className="shrink-0 text-6xl font-medium leading-none tracking-[-0.06em] text-black md:text-7xl">
								0.2
							</p>
							<p className="max-w-[16rem] text-sm font-semibold uppercase leading-5 tracking-[0.14em] text-black">
								Seconds to narrow search results
							</p>
						</div>
					</div>
				</div>
			</MarketingSection>

			<MarketingSection
				eyebrow="One connected platform"
				title="Everything works together to move shoppers forward."
			>
				<div className="grid grid-cols-1 border-l border-t border-black lg:grid-cols-3">
					{connectedPlatformFeatures.map((feature) => (
						<div
							className="border-b border-r border-black bg-[#fffffa]/60 p-7"
							key={feature.label}
						>
							<p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#EC1C23]">
								{feature.label}
							</p>
							<h3 className="mt-6 text-3xl font-medium">{feature.title}</h3>
							<p className="text-black/64 mt-7 text-sm leading-6">
								{feature.description}
							</p>
						</div>
					))}
				</div>
			</MarketingSection>

			<MarketingSection id="getStarted" className="scroll-mt-24">
				<div className="mx-auto max-w-[620px]">
					<div className="mb-8 text-center">
						<h2 className="text-4xl font-medium leading-tight text-black md:text-5xl">
							Schedule a Demo
						</h2>
						<p className="text-black/58 mx-auto mt-4 max-w-[520px] text-base leading-7 md:text-lg">
							Submit the form below or schedule a time directly on our calendar.
						</p>
					</div>
					<BookDemoTabbedForm
						isSubmitted={Boolean(didSubmit)}
						lastResult={lastResult}
						scheduleNamespace={directScheduleNamespace}
						successDescription="Your website demo request went to the DealerProEdge team. We will review your current site and follow up shortly."
						usePairedFieldRows
					/>
				</div>
			</MarketingSection>
			<MarketingSection title="Considering a different provider?">
				<p className="mb-7 max-w-2xl text-lg leading-8 text-black/70">
					Explore provider-specific guides, published capabilities, and the
					questions to bring to your next demo.
				</p>
				<MarketingButton to="/compare">
					Compare website providers
				</MarketingButton>
			</MarketingSection>
		</MarketingPageShell>
	)
}
